import { z } from 'zod';
import {
  PlainEventNameSchema,
  UITriggerSchema,
} from './ui-events';
import {
  DiscountTypeEnum,
  ExperienceTypeEnum,
  FRONT_STEP_TYPES,
  FrequencyCapEnum,
  FUNNEL_TYPE_ENTRY,
  FunnelTypeEnum,
  LayoutModeEnum,
  PlacementEnum,
  STEP_TYPE_EXPERIENCE_MAP,
  STEP_TYPE_MAX_COUNTS,
  StepTransitionEventEnum,
  StepTypeEnum,
  TAIL_STEP_TYPES,
  TrafficTypeEnum,
  TriggerLifecycleEnum,
  type FunnelType,
  type StepType,
} from '../taxonomy';
import { JsonValueSchema, NonEmptyStringSchema } from './common';
import { ManagedSemanticBehaviorSchema } from './managed-behavior';
import { PageBlockSchema, PageContentSchema } from './page-content';
import { StorefrontSemanticActionSchema } from './semantic-events';
import {
  StepConditionGroupSchema,
  StepConditionSchema,
  StepHookSchema,
} from './step-hook';

export { StepConditionGroupSchema, StepConditionSchema, StepHookSchema };

export const StepTransitionSchema = z.object({
  id: z.string().optional(),
  event: StepTransitionEventEnum,
  target: z.number().int().nonnegative(),
  priority: z.number().int().nonnegative().default(0),
  condition: StepHookSchema.optional(),
});

/**
 * WP-5: the WIRE shape of a step transition on the create/update-step write path
 * (funnels-go `normalizeStepTransitions`). Unlike `StepTransitionSchema` (which
 * targets a step POSITION for whole-funnel structure validation), the write path
 * targets a step by id (`targetStepId` — the funnels-go/MCP wire key; the
 * legacy `target_step_id` snake form is accepted and normalized) and
 * enum-validates the event so a
 * canon-invalid transition is rejected at the authoring boundary (MCP parity
 * with funnels-go's enum check) rather than silently stored.
 */
export const StepTransitionWriteSchema = z.preprocess(
  (value) => {
    // Accept the legacy snake form and normalize to the canonical wire key.
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      const record = value as Record<string, unknown>;
      if (record['targetStepId'] === undefined && record['target_step_id'] !== undefined) {
        const { target_step_id: snake, ...rest } = record;
        return { ...rest, targetStepId: snake };
      }
    }
    return value;
  },
  z.object({
    id: z.string().optional(),
    event: StepTransitionEventEnum,
    // Canonical wire key — funnels-go entities.StepTransition json:"targetStepId"
    // (transition.go) and the funnels-mcp typed client both use camelCase.
    targetStepId: NonEmptyStringSchema,
    priority: z.number().int().nonnegative().optional(),
    condition: StepHookSchema.nullable().optional(),
  }),
);

export type StepTransitionWrite = z.infer<typeof StepTransitionWriteSchema>;

const FunnelStepActionBaseSchema = z.object({
  id: z.string().optional(),
  label: z.string().optional(),
  hook: StepHookSchema.optional(),
  managedBehavior: ManagedSemanticBehaviorSchema.optional(),
});

export const StepActionUiConfigSchema = z
  .object({
    page_content: PageContentSchema.optional(),
    templateId: z.string().min(1).optional(),
    template_id: z.string().min(1).optional(),
    destinationEmail: z.string().email().optional(),
    fallbackEmail: z.string().email().optional(),
  })
  .strict()
  .refine(
    (config) =>
      config.page_content !== undefined ||
      config.templateId !== undefined ||
      config.template_id !== undefined,
    {
      message:
        'UI action config must include page_content, templateId, or template_id',
    },
  );

export const FunnelStepUiActionSchema = FunnelStepActionBaseSchema.extend({
  type: z.enum(['show_leads_popup', 'show_free_gift_popup', 'display_offer']),
  config: StepActionUiConfigSchema,
});

export const FunnelStepNonUiActionSchema = FunnelStepActionBaseSchema.extend({
  type: z.enum([
    'go_to_url',
    'connect_to_system',
    'dispatch_event',
    'save_to_storage',
  ]),
  config: z.record(JsonValueSchema).optional(),
});

export const FunnelStepActionSchema = z.union([
  FunnelStepUiActionSchema,
  FunnelStepNonUiActionSchema,
  StorefrontSemanticActionSchema,
]);

/**
 * Step listener reaction (ADR 0004 R16 = FULL). A listener may run an authored
 * action or drive navigation. Navigation always goes through the
 * server-validated accept / decline / go_to_step transitions — never a free
 * jump — so the runtime keeps a single reconciled state machine.
 */
export const StepListenerNavigateSchema = z
  .object({
    type: z.enum(['accept', 'decline', 'go_to_step']),
    stepId: NonEmptyStringSchema.optional(),
  })
  .superRefine((navigate, ctx) => {
    if (navigate.type === 'go_to_step' && navigate.stepId === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "navigate.stepId is required when type is 'go_to_step'",
        path: ['stepId'],
      });
    }
  });

export const StepListenerReactionSchema = z
  .object({
    kind: z.enum(['run_action', 'navigate']),
    action: FunnelStepActionSchema.optional(),
    navigate: StepListenerNavigateSchema.optional(),
  })
  .superRefine((reaction, ctx) => {
    if (reaction.kind === 'run_action' && reaction.action === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "reaction.action is required when kind is 'run_action'",
        path: ['action'],
      });
    }
    if (reaction.kind === 'navigate' && reaction.navigate === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "reaction.navigate is required when kind is 'navigate'",
        path: ['navigate'],
      });
    }
  });

/**
 * Step listener config. Carries the MVP `ListenerBinder` keys (selector /
 * scroll_depth / delay_ms / pixel_url / event_name) plus the optional ADR 0004
 * `reaction`. `.passthrough()` keeps any additional author-supplied keys so
 * pre-existing listener configs round-trip unchanged (backward compatible).
 */
export const StepListenerConfigSchema = z
  .object({
    selector: z.string().optional(),
    scroll_depth: z.number().optional(),
    delay_ms: z.number().int().nonnegative().optional(),
    pixel_url: z.string().optional(),
    event_name: z.string().optional(),
    sourceBlockId: NonEmptyStringSchema.optional(),
    sourceTrigger: UITriggerSchema.optional(),
    reaction: StepListenerReactionSchema.optional(),
  })
  .passthrough();

/**
 * Canonical step listener — served by funnels-go and persisted in the
 * `funnel_steps.listeners` JSONB column (tss-docs ac-02 / ac-05). A listener
 * subscribes by `eventName` to a PLAIN component event name (D-G; legacy
 * canonical `ui.` or legacy `funnel.ui.` stored values normalize on read) and
 * declares its behavior in `config`. `id` is server-assigned, hence optional at
 * authoring time.
 */
export const StepListenerSchema = z.object({
  id: z.string().optional(),
  eventName: PlainEventNameSchema,
  config: StepListenerConfigSchema,
  managedBehavior: ManagedSemanticBehaviorSchema.optional(),
});

export type StepListenerNavigate = z.infer<typeof StepListenerNavigateSchema>;
export type StepListenerReaction = z.infer<typeof StepListenerReactionSchema>;
export type StepListenerConfig = z.infer<typeof StepListenerConfigSchema>;
export type StepListener = z.infer<typeof StepListenerSchema>;

export const OfferStructureSchema = z.object({
  name: z.string().min(1),
  product_name: z.string().min(1),
  discount_type: DiscountTypeEnum,
  discount_value: z.number().positive(),
  headline: z.string().min(1),
  cta_text: z.string().min(1),
});

export const StepPageBlockSchema = PageBlockSchema;

export const StepPageContentSchema = PageContentSchema;

const FunnelStepStructureShape = {
  name: z.string().min(1),
  step_type: StepTypeEnum,
  position: z.number().int().nonnegative(),
  experience_type: ExperienceTypeEnum,
  placement: PlacementEnum,
  placement_url_pattern: z.string().optional(),
  is_entry: z.boolean().optional().default(false),
  frequency_cap: FrequencyCapEnum.default('unlimited'),
  layout_mode: LayoutModeEnum.optional().default('card'),
  offer: OfferStructureSchema.optional().nullable(),
  actions: z.array(FunnelStepActionSchema).optional(),
  listeners: z.array(StepListenerSchema),
  page_content: StepPageContentSchema.optional().nullable(),
  hook: StepHookSchema.optional().nullable(),
  accept_goes_to: z.number().int().nonnegative().nullable(),
  decline_goes_to: z.number().int().nonnegative().nullable(),
  transitions: z.array(StepTransitionSchema).optional(),
};

export const FunnelStepStructureSchema: z.ZodObject<
  typeof FunnelStepStructureShape
> = z.object(FunnelStepStructureShape);

type ValidatedFunnelStepForRefinement = z.infer<
  typeof FunnelStepStructureSchema
>;

type ValidatedFunnelStructureForRefinement = {
  funnel_type: FunnelType;
  steps: readonly ValidatedFunnelStepForRefinement[];
};

type IndexedFunnelStepForRefinement = {
  step: ValidatedFunnelStepForRefinement;
  index: number;
};

function transitionTargetsAreValid(
  steps: readonly ValidatedFunnelStepForRefinement[],
): boolean {
  const validPositions = new Set(steps.map((step) => step.position));
  return steps.every((step) =>
    (step.transitions ?? []).every((transition) =>
      validPositions.has(transition.target),
    ),
  );
}

function collectNavigationTargets(
  step: ValidatedFunnelStepForRefinement,
): number[] {
  const scalarTargets = [step.accept_goes_to, step.decline_goes_to].filter(
    (target): target is number => target !== null,
  );
  const transitionTargets = (step.transitions ?? []).map(
    (transition) => transition.target,
  );

  return [...scalarTargets, ...transitionTargets];
}

function findNavigationCycle(
  steps: readonly ValidatedFunnelStepForRefinement[],
): { from: number; to: number } | null {
  const graph = new Map(
    steps.map((step) => [step.position, collectNavigationTargets(step)]),
  );
  const visiting = new Set<number>();
  const visited = new Set<number>();

  function visit(position: number): { from: number; to: number } | null {
    visiting.add(position);

    for (const target of graph.get(position) ?? []) {
      if (!graph.has(target)) {
        continue;
      }

      if (visiting.has(target)) {
        return { from: position, to: target };
      }

      if (!visited.has(target)) {
        const cycle = visit(target);
        if (cycle !== null) {
          return cycle;
        }
      }
    }

    visiting.delete(position);
    visited.add(position);
    return null;
  }

  for (const position of graph.keys()) {
    if (!visited.has(position)) {
      const cycle = visit(position);
      if (cycle !== null) {
        return cycle;
      }
    }
  }

  return null;
}

function addStepTypeMaxCountIssues(
  steps: readonly ValidatedFunnelStepForRefinement[],
  ctx: z.RefinementCtx,
): void {
  const counts = new Map<ValidatedFunnelStepForRefinement['step_type'], number>();

  for (const step of steps) {
    counts.set(step.step_type, (counts.get(step.step_type) ?? 0) + 1);
  }

  for (const stepType of Object.keys(
    STEP_TYPE_MAX_COUNTS,
  ) as ValidatedFunnelStepForRefinement['step_type'][]) {
    const maxCount = STEP_TYPE_MAX_COUNTS[stepType];
    const count = counts.get(stepType) ?? 0;

    if (maxCount !== undefined && count > maxCount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['steps'],
        message: `step_type "${stepType}" appears ${count} times but is limited to ${maxCount} per funnel`,
      });
    }
  }
}

function isFrontOrCartStep(stepType: StepType): boolean {
  return (
    stepType === 'cart' ||
    FRONT_STEP_TYPES.some((frontStepType) => frontStepType === stepType)
  );
}

function isTailStep(stepType: StepType): boolean {
  return TAIL_STEP_TYPES.some((tailStepType) => tailStepType === stepType);
}

function sortStepsByPosition(
  steps: readonly ValidatedFunnelStepForRefinement[],
): IndexedFunnelStepForRefinement[] {
  return steps
    .map((step, index) => ({ step, index }))
    .sort((left, right) => left.step.position - right.step.position);
}

function addFunnelTypeStructureIssues(
  data: ValidatedFunnelStructureForRefinement,
  ctx: z.RefinementCtx,
): void {
  const sortedSteps = sortStepsByPosition(data.steps);
  const firstStep = sortedSteps[0];
  const expectedEntry = FUNNEL_TYPE_ENTRY[data.funnel_type];
  const checkoutStep = sortedSteps.find(
    ({ step }) => step.step_type === 'checkout',
  );
  const checkoutPosition = checkoutStep?.step.position;
  const tailSteps = sortedSteps.filter(({ step }) => isTailStep(step.step_type));
  const hasTailSteps = tailSteps.length > 0;
  const lastStep = sortedSteps[sortedSteps.length - 1];

  if (firstStep !== undefined && firstStep.step.step_type !== expectedEntry) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['steps', firstStep.index, 'step_type'],
      message: `first step type must match funnel_type entry: "${data.funnel_type}" expects "${expectedEntry}"`,
    });
  }

  sortedSteps.forEach(({ step, index }, sortedIndex) => {
    if (sortedIndex > 0 && step.is_entry) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['steps', index, 'is_entry'],
        message: 'is_entry is only allowed on the first step',
      });
    }
  });

  if (checkoutPosition !== undefined) {
    const frontOrCartAfterCheckout = sortedSteps.find(
      ({ step }) =>
        isFrontOrCartStep(step.step_type) && step.position > checkoutPosition,
    );

    if (frontOrCartAfterCheckout !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['steps', frontOrCartAfterCheckout.index, 'step_type'],
        message: 'front/cart steps must appear before checkout',
      });
    }

    const tailBeforeCheckout = tailSteps.find(
      ({ step }) => step.position <= checkoutPosition,
    );

    if (tailBeforeCheckout !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['steps', tailBeforeCheckout.index, 'step_type'],
        message: 'tail steps must appear after checkout',
      });
    }
  } else if (hasTailSteps) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['steps'],
      message: 'tail steps must appear after checkout',
    });
  }

  if (expectedEntry === 'checkout' && !hasTailSteps) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['steps'],
      message: 'checkout-entry funnels must include at least one tail step',
    });
  }

  if (lastStep !== undefined) {
    if (hasTailSteps && lastStep.step.step_type !== 'thank_you') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['steps', lastStep.index, 'step_type'],
        message: 'funnels with tail steps must terminate at thank_you',
      });
    }

    if (!hasTailSteps && lastStep.step.step_type !== 'checkout') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['steps', lastStep.index, 'step_type'],
        message: 'funnels without tail steps must terminate at checkout',
      });
    }
  }
}

const FunnelStructureBaseShape = {
  name: z.string().min(3),
  description: z.string(),
  funnel_type: FunnelTypeEnum,
  traffic_type: TrafficTypeEnum,
  flow_variant: z.enum(['main', 'ab', 'qa']).default('main'),
  countries: z.array(z.string().length(2).toUpperCase()).min(1),
  trigger_lifecycle: TriggerLifecycleEnum.default('session'),
  is_active: z.boolean(),
  offer_mode: z.enum(['step', 'funnel']).optional().default('step'),
  funnel_offer_id: z.string().nullable().optional(),
  activation_trigger: StepHookSchema.optional().nullable(),
  steps: z.array(FunnelStepStructureSchema).min(1),
};

const FunnelStructureBaseSchema: z.ZodObject<
  typeof FunnelStructureBaseShape
> = z.object(FunnelStructureBaseShape);

export const FunnelStructureSchema: z.ZodType<
  z.output<typeof FunnelStructureBaseSchema>,
  z.ZodTypeDef,
  z.input<typeof FunnelStructureBaseSchema>
> = FunnelStructureBaseSchema
  .refine(
    (data) => {
      const sortedPositions = [...data.steps.map((step) => step.position)].sort(
        (left, right) => left - right,
      );
      return sortedPositions.every((position, index) => position === index);
    },
    { message: 'Step positions must be sequential starting from 0' },
  )
  .refine(
    (data) => {
      const validPositions = new Set(data.steps.map((step) => step.position));
      return data.steps.every(
        (step) =>
          (step.accept_goes_to === null ||
            validPositions.has(step.accept_goes_to)) &&
          (step.decline_goes_to === null ||
            validPositions.has(step.decline_goes_to)),
      );
    },
    {
      message:
        'accept_goes_to and decline_goes_to must reference valid step positions',
    },
  )
  .refine(
    (data) => {
      const lastStep = data.steps.reduce((current, candidate) =>
        candidate.position > current.position ? candidate : current,
      );
      return (
        lastStep.accept_goes_to === null &&
        lastStep.decline_goes_to === null &&
        (lastStep.transitions === undefined || lastStep.transitions.length === 0)
      );
    },
    {
      message:
        'Last step must be terminal (no transitions and accept_goes_to/decline_goes_to must be null)',
    },
  )
  .refine(
    (data) =>
      data.steps.every((step) => {
        const allowed = STEP_TYPE_EXPERIENCE_MAP[step.step_type];
        return allowed === undefined || allowed.includes(step.experience_type);
      }),
    (data) => {
      const offending = data.steps.find((step) => {
        const allowed = STEP_TYPE_EXPERIENCE_MAP[step.step_type];
        return allowed !== undefined && !allowed.includes(step.experience_type);
      }) as z.infer<typeof FunnelStepStructureSchema>;
      const allowed = (
        STEP_TYPE_EXPERIENCE_MAP[offending.step_type] as readonly string[]
      ).join(' | ');
      const stepDesc = `step "${offending.name}" (${offending.step_type})`;
      return {
        message: `experience_type mismatch on ${stepDesc}: got "${offending.experience_type}", expected one of [${allowed}]`,
      };
    },
  )
  .refine((data) => transitionTargetsAreValid(data.steps), {
    message: 'transitions[].target must reference a valid step position',
  })
  .refine((data) => findNavigationCycle(data.steps) === null, (data) => {
    const cycle = findNavigationCycle(data.steps);
    const from = cycle === null ? -1 : cycle.from;
    const to = cycle === null ? -1 : cycle.to;
    return {
      // Future follow-up: add an explicit allow_cycles opt-in before permitting loops.
      message: `transitions form a cycle (step ${from} -> step ${to}); cycles are not allowed`,
    };
  })
  .superRefine((data, ctx) => addStepTypeMaxCountIssues(data.steps, ctx))
  .superRefine((data, ctx) => addFunnelTypeStructureIssues(data, ctx));

export type ValidatedFunnelStructure = z.infer<
  typeof FunnelStructureSchema
>;
export type ValidatedFunnelStep = z.infer<typeof FunnelStepStructureSchema>;
export type ValidatedStepTransition = z.infer<typeof StepTransitionSchema>;
export type ValidatedOffer = z.infer<typeof OfferStructureSchema>;
export type ValidatedStepActionUiConfig = z.infer<
  typeof StepActionUiConfigSchema
>;
export type ValidatedStepUiAction = z.infer<typeof FunnelStepUiActionSchema>;
export type ValidatedStepNonUiAction = z.infer<
  typeof FunnelStepNonUiActionSchema
>;
export type ValidatedStepAction = z.infer<typeof FunnelStepActionSchema>;
export type ValidatedStepPageBlock = z.infer<typeof StepPageBlockSchema>;
export type ValidatedStepPageContent = z.infer<typeof StepPageContentSchema>;
export type ValidatedStepCondition = z.infer<typeof StepConditionSchema>;
export type ValidatedConditionGroup = z.infer<
  typeof StepConditionGroupSchema
>;
export type ValidatedStepHook = z.infer<typeof StepHookSchema>;
